import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CardChevron } from '../components/CardChevron';
import { EmptyState } from '../components/EmptyState';
import {
	DEFAULT_HISTORY_RANGE,
	HistoryFilters,
	resolveHistoryRange,
} from '../components/HistoryFilterBar';
import { MobilePageHeader } from '../components/layout/MobilePageHeader';
import { Paginator } from '../components/Paginator';
import { HistoryListSkeleton } from '../components/skeletons';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { appScrollToTop } from '../lib/appScroll';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import * as portalStatementService from '../services/portalStatementService';
import { useLoginModalStore } from '../stores/loginModalStore';

const PAGE_SIZE = 20;

/**
 * Statement history (PD-14046) — the customer's saved statements across all
 * assigned clients, modeled on the invoices page. Click into a statement for
 * the on-screen rendering and PDF download.
 */
export function StatementsPage(): JSX.Element {
	const { t } = useTranslation();
	const { token, isAuthenticated, availableClients } = usePortalAuth();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const currentPage = parseInt(searchParams.get('page') ?? '1', 10);
	const clientIdParam = searchParams.get('clientId') ?? '';
	// Relative range key ('' = the default, last 3 months) resolved to
	// concrete ISO bounds for the API (PD-14674).
	const rangeParam = searchParams.get('range') ?? '';
	const { dateFrom, dateTo } = resolveHistoryRange(rangeParam);

	const { data, isLoading } = useQuery({
		queryKey: ['portal-statements', currentPage, clientIdParam, rangeParam],
		queryFn: () =>
			portalStatementService.getStatements(token ?? '', {
				page: currentPage,
				limit: PAGE_SIZE,
				clientId:
					clientIdParam.length > 0 ? parseInt(clientIdParam, 10) : undefined,
				dateFrom,
				dateTo,
			}),
		enabled: token != null,
		staleTime: 30_000,
	});

	const statements = data?.data ?? [];
	const pagination = data?.pagination ?? null;

	const updateParams = useCallback(
		(updates: Record<string, string | null>) => {
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				for (const [key, value] of Object.entries(updates)) {
					if (value == null || value.length === 0) {
						next.delete(key);
					} else {
						next.set(key, value);
					}
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const handlePageChange = useCallback(
		(page: number) => {
			updateParams({ page: page > 1 ? String(page) : null });
			appScrollToTop('smooth');
		},
		[updateParams],
	);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	if (!isAuthenticated) {
		return <div />;
	}

	return (
		<div className="px-4 py-4 max-w-4xl mx-auto">
			<MobilePageHeader title="Statements" backPath="/account" />
			<h1 className="hidden md:block text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
				{t('Statements')}
			</h1>

			{/* One filter tray (date range / location) with Apply-Cancel
			    (PD-14701). No keyword search on statements. */}
			<div className="mb-4">
				<HistoryFilters
					fields={[
						{ kind: 'range', key: 'range', label: 'Date Range' },
						...(availableClients.length > 1
							? [
									{
										kind: 'location' as const,
										key: 'clientId' as const,
										label: 'Location',
										clients: availableClients,
									},
								]
							: []),
					]}
					values={{ range: rangeParam, clientId: clientIdParam }}
					onApply={(next) => {
						const range = next.range;
						const clientId = next.clientId;
						updateParams({
							range:
								range.length > 0 && range !== DEFAULT_HISTORY_RANGE
									? range
									: null,
							clientId: clientId.length > 0 ? clientId : null,
							page: null,
						});
					}}
				/>
			</div>

			{isLoading ? (
				/* Skeleton cards shaped like the list (PD-14692) */
				<HistoryListSkeleton />
			) : statements.length === 0 ? (
				/* Empty state (PD-14699) — filtered-empty variants first. */
				clientIdParam.length > 0 ? (
					<EmptyState
						icon={faFileLines}
						title="No statements for this location"
						hint="Try removing the location filter to see more results."
						actionLabel="Clear filter"
						onAction={() => {
							updateParams({ clientId: null, page: null });
						}}
					/>
				) : rangeParam !== 'all' ? (
					<EmptyState
						icon={faFileLines}
						title="No statements in this date range"
						hint="Older statements may fall outside the selected range."
						actionLabel="View all time"
						onAction={() => {
							updateParams({ range: 'all', page: null });
						}}
					/>
				) : (
					<EmptyState
						icon={faFileLines}
						title="No statements yet"
						hint="Your statements will appear here once they are issued."
					/>
				)
			) : (
				<div className="flex flex-col gap-3">
					{statements.map((statement) => (
						<button
							key={statement.id}
							type="button"
							onClick={() => {
								void navigate(`/statements/${statement.id}`);
							}}
							className="w-full text-left bg-brand-surface border border-brand-border rounded-xl p-4 hover:border-portal-accent transition-colors min-h-touch"
						>
							<div className="flex items-start justify-between gap-3">
								<div className="min-w-0">
									<div className="font-semibold text-gray-900 dark:text-gray-100">
										{t(statement.statementNumber)}
									</div>
									{availableClients.length > 1 && (
										<div className="text-sm text-brand-muted truncate">
											{t(statement.clientName)}
										</div>
									)}
									<div className="text-sm text-brand-muted">
										{t('As of')}{' '}
										{formatStatementDate(
											statement.asOfDate as unknown as string,
										)}
									</div>
								</div>
								<div className="text-right shrink-0">
									<div className="font-bold text-gray-900 dark:text-gray-100">
										{formatCurrency(statement.totalAmountDue)}
									</div>
									<div className="text-xs uppercase tracking-wide text-brand-muted">
										{t('Amount Due')}
									</div>
								</div>
								<CardChevron />
							</div>
						</button>
					))}
				</div>
			)}

			{pagination != null && pagination.totalPages > 1 && (
				<div className="mt-4">
					<Paginator
						currentPage={pagination.page}
						totalPages={pagination.totalPages}
						onPageChange={handlePageChange}
					/>
				</div>
			)}
		</div>
	);
}
